'use client'

import { useState } from 'react'
import Link from 'next/link'

type FooterLink = {
  label: string
  href: string
  badge?: string
  status?: boolean
}

const footerLinks: Record<string, FooterLink[]> = {
  Platform: [
    { label: 'Market', href: '/app/market' },
    { label: 'Search', href: '/app/search' },
    { label: 'API', href: '#' },
    { label: 'Integrations', href: '#' },
  ],
  Data: [
    { label: 'Auction Records', href: '/app/market' },
    { label: 'Artist Database', href: '/app/artists' },
    { label: 'Price Index', href: '#' },
    { label: 'Market Reports', href: '#' },
  ],
  Resources: [
    { label: 'Documentation', href: '#' },
    { label: 'Tutorials', href: '#' },
    { label: 'Case Studies', href: '#' },
    { label: 'Blog', href: '#' },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'Get Started', href: '/get-started' },
    { label: 'Contact', href: '#' },
  ],
  Support: [
    { label: 'AI Assistant', href: '#chat', badge: '24/7' },
    { label: 'Help Center', href: '#' },
    { label: 'Status', href: '#status', status: true },
  ],
  Legal: [
    { label: 'Privacy', href: '#' },
    { label: 'Terms', href: '#' },
    { label: 'Security', href: '#' },
  ],
}

const socialLinks = [
  { name: 'LinkedIn', href: '#', icon: 'M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z' },
  { name: 'X', href: '#', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
  { name: 'Instagram', href: '#', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' },
]

const regions = [
  { code: 'en-US', label: 'English (US)', flag: '🇺🇸' },
  { code: 'en-GB', label: 'English (UK)', flag: '🇬🇧' },
  { code: 'zh-HK', label: '中文 (香港)', flag: '🇭🇰' },
  { code: 'de-DE', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr-FR', label: 'Français', flag: '🇫🇷' },
]

export function Footer() {
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)
  const [region, setRegion] = useState(regions[0])
  const [showRegions, setShowRegions] = useState(false)

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      setSubscribed(true)
      setEmail('')
    }
  }

  return (
    <footer className="py-16 px-6 border-t border-border bg-page">
      <div className="max-w-7xl mx-auto">
        {/* Top section: Newsletter + Description */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Left: Logo + Description */}
          <div>
            <Link href="/" className="flex items-baseline gap-0.5 mb-4">
              <span className="font-display text-3xl font-light text-text-primary">
                salle
              </span>
              <span className="text-lg text-text-tertiary">.art</span>
            </Link>
            <p className="text-text-secondary text-sm max-w-md mb-6">
              The Bloomberg of fine art. AI-native market intelligence across 85 million
              auction lots, helping collectors, advisors, and institutions make informed decisions.
            </p>

            {/* Powered by Karla */}
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <span>Data powered by</span>
              <span className="font-medium text-text-secondary">Karla</span>
              <span className="text-text-muted">·</span>
              <span>Collect any data. From anywhere.</span>
            </div>
          </div>

          {/* Right: Newsletter */}
          <div className="lg:pl-12">
            <h4 className="font-medium text-text-primary mb-2">Stay informed</h4>
            <p className="text-text-tertiary text-sm mb-4">
              Weekly market insights, emerging artists, and auction highlights.
            </p>
            {subscribed ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--positive)' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Thanks for subscribing!
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-full border border-border bg-card text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-border-hover"
                  required
                />
                <button
                  type="submit"
                  className="pill shrink-0"
                  style={{ background: 'var(--accent-primary)', color: 'white' }}
                >
                  Subscribe
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Links grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-medium text-text-primary mb-4 text-sm">{category}</h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-text-tertiary hover:text-text-secondary transition-colors text-sm flex items-center gap-1"
                    >
                      {link.label}
                      {link.badge && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-accent-primary/10" style={{ color: 'var(--accent-primary)' }}>
                          {link.badge}
                        </span>
                      )}
                      {link.status && (
                        <span className="w-2 h-2 rounded-full bg-positive" />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center gap-6 mb-12 py-6 border-y border-border-subtle">
          <div className="flex items-center gap-2 text-xs text-text-tertiary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>SOC 2 Type II</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-text-tertiary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>GDPR Compliant</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-text-tertiary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            <span>256-bit Encryption</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-text-tertiary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>99.9% Uptime SLA</span>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Left: Social + Region */}
          <div className="flex items-center gap-6">
            {/* Social links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="text-text-tertiary hover:text-text-secondary transition-colors"
                  aria-label={social.name}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d={social.icon} />
                  </svg>
                </a>
              ))}
            </div>

            {/* Region selector */}
            <div className="relative">
              <button
                onClick={() => setShowRegions(!showRegions)}
                className="flex items-center gap-2 text-sm text-text-tertiary hover:text-text-secondary transition-colors"
              >
                <span>{region.flag}</span>
                <span>{region.label}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showRegions && (
                <div className="absolute bottom-full mb-2 left-0 bg-card rounded-xl shadow-lg border border-border py-2 min-w-[180px]">
                  {regions.map((r) => (
                    <button
                      key={r.code}
                      onClick={() => {
                        setRegion(r)
                        setShowRegions(false)
                      }}
                      className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                        region.code === r.code ? 'text-text-primary bg-page' : 'text-text-secondary hover:bg-page'
                      }`}
                    >
                      <span>{r.flag}</span>
                      <span>{r.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Copyright */}
          <p className="text-sm text-text-tertiary">
            © {new Date().getFullYear()} salle.art. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
